import { Box } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { ServerHighlightGeom } from '../pages/RO/AiSummary/components/Interface';
import { openSafeUrl } from './openSafeUrl';

export interface PdfDimensions {
  width: number;
  height: number;
  rotation?: number;
}

export type PdfDimensionMap = Record<number, PdfDimensions>;

export interface PDFRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
  pageWidth: number;
  pageHeight: number;
  pageNumber: number;
}

export interface HighlightPosition {
  boundingRect: PDFRect;
  rects: PDFRect[];
  pageNumber: number;
}

export interface GeneratedHighlight {
  id: string;
  position: HighlightPosition;
  content: { text: string };
  comment: { text: string; emoji: string };
}

export interface CitationHighlightProps {
  url?: string;
  title?: string;
  height?: string | number;
  width?: string | number;
  onClose?: () => void;
  status?: string;
  highlightsGeoJson?: ServerHighlightGeom[];
  dateOfDocument?: string;
  disableAnnotations?: boolean;
  icon?: React.ReactNode;
  documentType?: string;
  author?: string;
  isOverlay?: boolean;
}

const sanitizeFilename = (name: string): string => name.replace(/[/\\?%*:|"<>\x00-\x1f]/g, '_');

export const extractFilenameFromUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.href);
    const pathSegment = parsed.pathname.split('/').pop() || 'document';
    return sanitizeFilename(decodeURIComponent(pathSegment));
  } catch {
    const withoutQuery = url.split('?')[0];
    const segment = withoutQuery.split('/').pop() || 'document';
    try {
      return sanitizeFilename(decodeURIComponent(segment));
    } catch {
      return sanitizeFilename(segment);
    }
  }
};

const getFilenameFromDisposition = (disposition: string | null, fallback: string): string => {
  if (!disposition) {
    return fallback;
  }

  const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  if (filenameMatch && filenameMatch[1]) {
    try {
      return sanitizeFilename(decodeURIComponent(filenameMatch[1]));
    } catch {
      return sanitizeFilename(filenameMatch[1]);
    }
  }

  return fallback;
};

export const downloadDocument = async (url: string): Promise<void> => {
  try {
    const response = await fetch(url, { method: 'GET', mode: 'cors' });
    if (!response.ok) {
      throw new Error('Failed to download document');
    }

    const blob = await response.blob();
    const fallbackName = extractFilenameFromUrl(url);
    const disposition = response.headers.get('content-disposition');
    const filename = getFilenameFromDisposition(disposition, fallbackName);

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    openSafeUrl(url);
  }
};

const isPdfPath = (targetUrl: string): boolean => {
  try {
    const parsed = new URL(targetUrl, window.location.href);
    return parsed.pathname.toLowerCase().endsWith('.pdf');
  } catch (error) {
    return targetUrl.toLowerCase().includes('.pdf');
  }
};

export const shouldOpenPdfInline = async (url: string): Promise<boolean> => {
  if (isPdfPath(url)) {
    return true;
  }

  try {
    const response = await fetch(url, { method: 'HEAD', mode: 'cors' });
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.toLowerCase().includes('pdf')) {
      return true;
    }

    const disposition = response.headers.get('content-disposition');
    if (disposition && disposition.toLowerCase().includes('.pdf')) {
      return true;
    }
  } catch (error) {}

  return false;
};

export const openPdfInNewTab = (url: string): void => {
  openSafeUrl(url);
};

export const ViewerContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  backgroundColor: theme.palette.background.default,
}));

export const ToolbarContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
}));

export const PDFContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  width: '100%',

  '& .react-pdf__Page': { margin: '0 auto' },
  '& .react-pdf__Page__canvas': { display: 'block', margin: '0 auto' },
  '& .Highlight__part': { backgroundColor: 'rgba(251, 188, 5, 0.25) !important' },

  // Target the PdfHighlighter wrapper - this is the scrollable container
  '& > div': {
    position: 'absolute !important',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'auto !important',
    paddingBottom: '100px !important',
  },

  // Ensure the internal viewer container can scroll properly
  '& .pdfViewer': {
    padding: '0 !important',
    overflow: 'visible !important',
    paddingBottom: '100px !important',
  },

  // Add margin to last page for extra safety
  '& .page:last-child': {
    marginBottom: '100px !important',
  },
}));

// Transform normalized coordinates based on PDF page rotation
const transformCoordinates = (coordinates: number[], rotation: number): number[] => {
  const [x, y] = coordinates;
  switch (rotation) {
    case 90:
      return [y, 1 - x];
    case 180:
      return [1 - x, 1 - y];
    case 270:
      return [1 - y, x];
    case 0:
    default:
      return [x, y];
  }
};

// Transform geometry coordinates based on rotation
const transformGeometry = (geometry: any, rotation: number): any => {
  if (!geometry || !geometry.coordinates || rotation === 0) return geometry;

  const transformCoordArray = (coords: number[][]): number[][] =>
    coords.map((coord) => transformCoordinates(coord, rotation));

  const transformedGeometry = { ...geometry };

  if (geometry.type === 'Polygon') {
    transformedGeometry.coordinates = geometry.coordinates.map((ring: number[][]) => transformCoordArray(ring));
  } else if (geometry.type === 'MultiPolygon') {
    transformedGeometry.coordinates = geometry.coordinates.map((polygon: number[][][]) =>
      polygon.map((ring: number[][]) => transformCoordArray(ring)),
    );
  }

  return transformedGeometry;
};

export const mergeHorizontalRects = (rects: PDFRect[]): PDFRect[] => {
  if (rects.length <= 1) return rects;

  const sorted = [...rects].sort((a, b) => {
    const yDiff = a.y1 - b.y1;
    return yDiff !== 0 ? yDiff : a.x1 - b.x1;
  });

  const merged: PDFRect[] = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];

    const sameLine = Math.abs(current.y1 - next.y1) < 1 && Math.abs(current.y2 - next.y2) < 1;
    const horizontalGap = next.x1 - current.x2;
    const adjacent = horizontalGap >= -1 && horizontalGap <= 4;

    if (sameLine && adjacent) {
      current.x2 = Math.max(current.x2, next.x2);
      current.y1 = Math.min(current.y1, next.y1);
      current.y2 = Math.max(current.y2, next.y2);
    } else {
      merged.push(current);
      current = { ...next };
    }
  }
  merged.push(current);

  return merged;
};

export const createHighlightsFromGeoJson = (
  geojson: ServerHighlightGeom,
  pdfDimensions: PdfDimensionMap | null,
): GeneratedHighlight[] => {
  if (!geojson?.geometry || !pdfDimensions) return [];

  const pageDimensions = pdfDimensions[geojson.pageNumber];
  if (!pageDimensions) return [];

  const rotation = pageDimensions.rotation || 0;
  const geometry = transformGeometry(geojson.geometry, rotation);
  const pdfWidth = pageDimensions.width;
  const pdfHeight = pageDimensions.height;

  let polygons: number[][][][] = [];

  if (geometry.type === 'Polygon') {
    const coords = geometry.coordinates as number[][][];
    if (coords && Array.isArray(coords)) {
      polygons = [coords];
    }
  } else if (geometry.type === 'MultiPolygon') {
    const coords = geometry.coordinates as number[][][][];
    if (coords && Array.isArray(coords)) {
      polygons = coords;
    }
  }

  if (polygons.length === 0) return [];

  const allHighlights: GeneratedHighlight[] = [];

  for (let polyIndex = 0; polyIndex < polygons.length; polyIndex++) {
    const polygon = polygons[polyIndex];
    if (!polygon || polygon.length === 0) continue;

    const ring = polygon[0];
    if (!Array.isArray(ring) || ring.length < 3) continue;

    const scaledRing: Array<[number, number]> = [];
    for (const coord of ring) {
      if (Array.isArray(coord) && coord.length >= 2) {
        scaledRing.push([coord[0] * pdfWidth, coord[1] * pdfHeight]);
      }
    }

    if (scaledRing.length < 3) continue;

    let minY = Infinity;
    let maxY = -Infinity;
    for (const [, y] of scaledRing) {
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    const heightRange = maxY - minY;
    const scanlineStep = 3; // Increased from 1px to 3px for better performance
    const numScans = Math.ceil(heightRange / scanlineStep);

    const rawRects: PDFRect[] = [];

    for (let i = 0; i < numScans; i++) {
      const y1 = minY + i * scanlineStep;
      const y2 = Math.min(y1 + scanlineStep, maxY);
      const scanY = (y1 + y2) / 2;

      const intersections: number[] = [];
      for (let j = 0; j < scaledRing.length; j++) {
        const [x1, y1Pt] = scaledRing[j];
        const [x2, y2Pt] = scaledRing[(j + 1) % scaledRing.length];

        const crosses = (y1Pt <= scanY && scanY < y2Pt) || (y2Pt <= scanY && scanY < y1Pt);

        if (crosses && Math.abs(y2Pt - y1Pt) > 0.001) {
          const t = (scanY - y1Pt) / (y2Pt - y1Pt);
          intersections.push(x1 + t * (x2 - x1));
        }
      }

      intersections.sort((a, b) => a - b);

      for (let k = 0; k + 1 < intersections.length; k += 2) {
        let xLeft = intersections[k];
        let xRight = intersections[k + 1];

        xLeft = Math.round(xLeft);
        xRight = Math.round(xRight);

        if (xRight - xLeft >= 6) {
          const x1 = Math.max(0, Math.min(pdfWidth, xLeft));
          const y1Coord = Math.max(0, Math.min(pdfHeight, y1));
          const x2 = Math.max(0, Math.min(pdfWidth, xRight));
          const y2Coord = Math.max(0, Math.min(pdfHeight, y2));

          rawRects.push({
            x1,
            y1: y1Coord,
            x2,
            y2: y2Coord,
            width: pdfWidth,
            height: pdfHeight,
            pageWidth: pdfWidth,
            pageHeight: pdfHeight,
            pageNumber: geojson.pageNumber,
          });
        }
      }
    }

    if (rawRects.length === 0) continue;

    const sortedByY = [...rawRects].sort((a, b) => a.y1 - b.y1);

    const lines: PDFRect[][] = [];
    let currentLine: PDFRect[] = [sortedByY[0]];

    for (let i = 1; i < sortedByY.length; i++) {
      const rect = sortedByY[i];
      const lastRect = currentLine[currentLine.length - 1];

      if (Math.abs(rect.y1 - lastRect.y1) <= 3) {
        currentLine.push(rect);
      } else {
        lines.push(currentLine);
        currentLine = [rect];
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    const lineStats = lines.map((line) => {
      const avgY = line.reduce((sum, r) => sum + r.y1, 0) / line.length;
      const totalWidth = line.reduce((sum, r) => sum + (r.x2 - r.x1), 0);
      const avgWidth = totalWidth / line.length;
      const minX = Math.min(...line.map((r) => r.x1));
      const maxX = Math.max(...line.map((r) => r.x2));
      return { line, avgY, avgWidth, totalWidth, minX, maxX };
    });

    const gaps: number[] = [];
    for (let i = 0; i < lineStats.length - 1; i++) {
      const gap = lineStats[i + 1].avgY - lineStats[i].avgY;
      gaps.push(gap);
    }

    const sortedGaps = [...gaps].sort((a, b) => a - b);
    const medianGap = sortedGaps.length > 0 ? sortedGaps[Math.floor(sortedGaps.length / 2)] : 12;

    const validLines: PDFRect[][] = [];
    for (let i = 0; i < lineStats.length; i++) {
      const stats = lineStats[i];

      const gapBefore = i > 0 ? stats.avgY - lineStats[i - 1].avgY : 0;
      const gapAfter = i < lineStats.length - 1 ? lineStats[i + 1].avgY - stats.avgY : 0;

      const inLargeGap = gapBefore > medianGap * 2 && gapAfter > medianGap * 2;
      const sparseContent = stats.totalWidth < 50;

      if (!inLargeGap && !sparseContent) {
        validLines.push(stats.line);
      }
    }

    if (validLines.length === 0) continue;

    const filteredRects = validLines.flat();

    const verticallyMerged: PDFRect[] = [];
    const sortedByX = [...filteredRects].sort((a, b) => a.x1 - b.x1 || a.y1 - b.y1);

    for (const rect of sortedByX) {
      const canMerge = verticallyMerged.find(
        (existing) =>
          Math.abs(existing.x1 - rect.x1) < 2 &&
          Math.abs(existing.x2 - rect.x2) < 2 &&
          Math.abs(existing.y2 - rect.y1) < 2,
      );

      if (canMerge) {
        canMerge.y2 = Math.max(canMerge.y2, rect.y2);
      } else {
        verticallyMerged.push({ ...rect });
      }
    }

    const mergedRects = mergeHorizontalRects(verticallyMerged);

    if (mergedRects.length === 0) continue;

    const x1 = Math.min(...mergedRects.map((r) => r.x1));
    const y1 = Math.min(...mergedRects.map((r) => r.y1));
    const x2 = Math.max(...mergedRects.map((r) => r.x2));
    const y2 = Math.max(...mergedRects.map((r) => r.y2));

    const boundingRect: PDFRect = {
      x1,
      y1,
      x2,
      y2,
      width: pdfWidth,
      height: pdfHeight,
      pageWidth: pdfWidth,
      pageHeight: pdfHeight,
      pageNumber: geojson.pageNumber,
    };

    // Use crypto.randomUUID if available, otherwise fall back to current method
    const uniqueId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    allHighlights.push({
      id: `highlight-${geojson.pageNumber}-poly${polyIndex}-${uniqueId}`,
      position: { boundingRect, rects: mergedRects, pageNumber: geojson.pageNumber },
      content: { text: '' },
      comment: { text: '', emoji: '' },
    });
  }

  return allHighlights;
};
